import React, { Component } from 'react';
import withStyles from 'react-jss';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import FuzzySearchBar from '../../../../career-search/FuzzySearchBar';
import PlusIcon from '../../../../utility/PlusIcon';
import { getFilteredListOfClasses } from '../dragDropUtil';
import { middle } from '../../../../../copy/ecap/classesSetupCopy.json';
import { coreSubjectsEn } from '../../../../../copy/ecap/classesSetupStrings';

const grid = 8;

const getClassListItemStyle = (
  virtualListStyle,
  draggableStyle,
  borderColor,
  isDragging
) => {
  const combinedStyles = {
    ...draggableStyle,
    ...virtualListStyle,
  };

  const marginBottom = 5;

  return {
    ...combinedStyles,
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    border: `2px solid ${borderColor}`,
    borderRadius: '9px',
    color: 'black',
    fontSize: '11px',
    backgroundColor: 'white',
    boxShadow: isDragging ? '0px 5px 5px grey' : null,
    overflow: 'visible',
    alignItems: 'center',
    height: isDragging
      ? combinedStyles.height
      : combinedStyles.height - marginBottom,
    display: 'flex',
  };
};

const customInputStyles = {
  textTitleColor: (props) => ({
    color: props.tenant.primary,
  }),
};

function Item({
  primary,
  provided,
  course,
  isDragging,
  style,
  subjectSearching,
  columnInd,
  addClass,
}) {
  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div
      className="class-list-draggable class-list-draggable-mobile"
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={getClassListItemStyle(
        style,
        provided.draggableProps.style,
        primary,
        isDragging
      )}
    >
      {Object.values(coreSubjectsEn)[course.academic_area_id - 1] ===
        subjectSearching ||
      subjectSearching === 'Electives' ||
      subjectSearching === 'Additional Electives' ? (
        <div
          className="class-list-draggable-text-mobile"
          role="button"
          tabIndex={0}
          onClick={() =>
            addClass({
              source: {
                droppableId: 'classList',
                title: course.requirement_title,
              },
              destination: {
                droppableId: `${subjectSearching}_${columnInd}`,
                index: columnInd,
              },
              course,
            })
          }
          onKeyDown={(e) => {
            if (['Enter'].includes(e.key)) {
              addClass({
                source: {
                  droppableId: 'classList',
                  title: course.requirement_title,
                },
                destination: {
                  droppableId: `${subjectSearching}_${columnInd}`,
                  index: columnInd,
                },
                course,
              });
            }
          }}
        >
          <p className="truncated-class-text">{course.requirement_title}</p>
          <PlusIcon color={primary} />
        </div>
      ) : (
        <div className="class-list-draggable-text-mobile">
          <p className="truncated-class-text">{course.requirement_title}</p>
        </div>
      )}
    </div>
  );
  /* eslint-enable react/jsx-props-no-spreading */
}

function createTenantRow(primary, subjectSearching, columnInd, addClass) {
  return (props) => {
    const { data: items, index, style } = props;
    const course = items[index];

    return (
      <Draggable
        key={`unplaced-${course.requirement_title}-${index}`}
        draggableId={`${course.class_id}`}
        index={index}
        isDragDisabled
      >
        {(provided) => (
          <Item
            provided={provided}
            course={course}
            primary={primary}
            style={style}
            subjectSearching={subjectSearching}
            columnInd={columnInd}
            addClass={addClass}
          />
        )}
      </Draggable>
    );
  };
}

class ClassListColumnMobile extends Component {
  constructor(props) {
    super(props);
    this.Row = createTenantRow(
      this.props.tenant.primary,
      this.props.subjectSearching,
      this.props.columnInd,
      this.props.addClass
    );
    this.state = {
      searchResults: [],
    };
  }

  sendFilteredListToParent = (val) => {
    this.props.searchUpdateClassList(val);
  };

  get searchResults() {
    return this.state.searchResults;
  }

  get classList() {
    return this.searchResults && this.searchResults.length > 0
      ? getFilteredListOfClasses(this.searchResults, this.placedClasses)
      : this.listOfClasses;
  }

  get placedClasses() {
    return this.props.placedClasses;
  }

  get allClasses() {
    return this.props.allClasses;
  }

  isDroppable = (course, subjectSearching) => {
    return (
      Object.values(coreSubjectsEn)[course.academic_area_id - 1] ===
        subjectSearching ||
      subjectSearching === 'Electives' ||
      subjectSearching === 'Additional Electives'
    );
  };

  get listOfClasses() {
    const availableClasses = getFilteredListOfClasses(
      this.allClasses,
      this.placedClasses
    );
    const filtered = availableClasses.filter((x) =>
      this.isDroppable(x, this.props.subjectSearching)
    );
    return filtered;
  }

  setSearchResults = (searchResults) => {
    return this.setState({ searchResults });
  };

  render() {
    return (
      <>
        <div className="search-bar-class-list-container">
          <FuzzySearchBar
            tenant={this.props.tenant}
            listToSearch={this.listOfClasses}
            sendFilteredListToParent={this.setSearchResults}
            // Need this prop in FuzzySearchBar but just use an anonymous function for now
            setSearchListLoadingOnParent={() => {}}
            overRideStylesSearchBar="ecap-classes-search-bar"
            inputText="Search"
            overRideStylesMag="ecap-mag-glass-mobile"
            searchKeys={['requirement_title']}
            mobile
          />
          <Droppable
            droppableId="classList"
            mode="virtual"
            isDropDisabled
            renderClone={(provided, snapshot, rubric) => (
              <Item
                provided={provided}
                isDragging={snapshot.isDragging}
                primary={this.props.tenant.primary}
                course={
                  this.props.filteredClassListsForSearch.length > 0
                    ? this.props.filteredClassListsForSearch[
                        rubric.source.index
                      ]
                    : this.props.listOfClasses[rubric.source.index]
                }
              />
            )}
          >
            {(provided) => (
              <AutoSizer defaultWidth={1} defaultHeight={1}>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    itemCount={this.classList.length}
                    itemSize={60}
                    width={width}
                    outerRef={provided.innerRef}
                    itemData={this.classList}
                  >
                    {this.Row}
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
          </Droppable>
        </div>
      </>
    );
  }
}

export default withStyles(customInputStyles)(ClassListColumnMobile);
